/**
 * What the pool tiers mean for a deployment: which products use them, what is submitted, and what it costs.
 * The numbers themselves live in data/sizeCatalog.ts and are only read here.
 */
import { hasProductLine, type ProductSubject } from '@/data/productLines'
import { getPoolTier, resolveVm, type PoolTierId, type VmKind, type VmSpec } from '@/data/sizeCatalog'
import type { InfrastructurePayload } from '@/types/infrastructure'

/**
 * Products whose RDS pool tier (and so its VMs) comes from the size catalog: the product lines (Traverse and Process Pro; see
 * data/productLines.ts). Ross and AppCentral keep sizing from the platform's own size configs.
 */
export function usesPoolTiers(product: ProductSubject): boolean {
  return hasProductLine(product)
}

/**
 * True when the chosen infrastructure builds a new SQL VM: a Dedicated DB server on the Domain level. An existing server and the
 * AppCentral level's dedicated Azure SQL (PaaS) server are not sized.
 */
export function createsNewSqlVm(infrastructure: Pick<InfrastructurePayload, 'dbServer'> | null | undefined): boolean {
  const db = infrastructure?.dbServer
  return !!db && db.mode === 'dedicated' && db.kind === 'sqlVm'
}

/** What is submitted for one VM. The cost is null while its price is not set. */
export interface VmSizingPayload {
  azureVmSku: string
  vcpus: number
  memoryGb: number
  estCostUsdPerMonth: number | null
  provisional: boolean
}

const vmPayload = (spec: Readonly<VmSpec>): VmSizingPayload => ({
  azureVmSku: spec.sku,
  vcpus: spec.vcpus,
  memoryGb: spec.memoryGb,
  estCostUsdPerMonth: spec.estCostUsdPerMonth,
  provisional: spec.provisional,
})

/** The sizing a deployment submits: the pool tier and its sessions, the RDS VM, and the DB VM only when a new SQL VM is built. */
export interface SizingPayload {
  poolTier: PoolTierId
  poolTierLabel: string
  sessions: number
  rds: VmSizingPayload
  db?: VmSizingPayload
}

export function resolveSizing(poolTier: PoolTierId, newSqlVm: boolean): SizingPayload {
  const entry = getPoolTier(poolTier)
  const sizing: SizingPayload = { poolTier, poolTierLabel: entry.label, sessions: entry.sessions, rds: vmPayload(resolveVm('rds', poolTier)) }
  if (newSqlVm) sizing.db = vmPayload(resolveVm('db', poolTier))
  return sizing
}

/** The sizing to submit, or undefined when no selected product uses the pool tiers. */
export function sizingForDeployment(
  products: readonly { product: { slug?: string; name?: string } }[],
  poolTier: PoolTierId,
  infrastructure: InfrastructurePayload | null | undefined,
): SizingPayload | undefined {
  if (!products.some((entry) => usesPoolTiers(entry.product))) return undefined
  return resolveSizing(poolTier, createsNewSqlVm(infrastructure))
}

export interface SizingCostLine {
  tier: VmKind
  label: string
  sku: string
  /** Null while the price is not set: the line says so instead of a number. */
  monthlyUsd: number | null
  /** True for the DB line: its cost leaves out SQL licensing. */
  excludesSqlLicensing: boolean
}

export interface SizingCost {
  lines: SizingCostLine[]
  /** The sum, or null while any line has no price: a partial sum would be read as the total. */
  totalUsd: number | null
  /** The VMs whose price is not set. */
  pending: string[]
  /** True when a DB line is part of the estimate, so it leaves out SQL licensing too. */
  excludesSqlLicensing: boolean
}

const COST_LABELS: Readonly<Record<VmKind, string>> = { rds: 'RDS VM', db: 'DB VM' }

/** The RDS VM line, plus the DB VM line when a new SQL VM is built, and their total (null while a price is not set). */
export function sizingCost(poolTier: PoolTierId, newSqlVm: boolean): SizingCost {
  const kinds: VmKind[] = newSqlVm ? ['rds', 'db'] : ['rds']
  const lines: SizingCostLine[] = kinds.map((kind) => {
    const spec = resolveVm(kind, poolTier)
    return { tier: kind, label: COST_LABELS[kind], sku: spec.sku, monthlyUsd: spec.estCostUsdPerMonth, excludesSqlLicensing: kind === 'db' }
  })
  const pending = lines.filter((line) => line.monthlyUsd === null).map((line) => line.label)
  const totalUsd = pending.length > 0 ? null : lines.reduce((sum, line) => sum + (line.monthlyUsd ?? 0), 0)
  return { lines, totalUsd, pending, excludesSqlLicensing: newSqlVm }
}
